import {
  NgModule,
  NO_ERRORS_SCHEMA,
  CUSTOM_ELEMENTS_SCHEMA
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MarketDrawerModule } from '../market-drawer/market-drawer.module';
import { GamedaySharedModule } from '../shared/shared.module';
import { GameDayComponent } from './components/gameday/gameDay.component';
import { OverviewComponent } from './components/overview/overview.component';
import { CardsHolderComponent } from './components/cards-holder/cards-holder.component';
import { NegativeCasesService } from './services/negative-cases.service';
import { DealSummaryCardComponent } from './components/deal-summary-card/deal-summary-card.component';
import { RecentOrdersComponent } from './components/recent-orders/recent-orders.component';
import { TopOrdersCardComponent } from './components/top-orders-card/top-orders-card.component';
import { CountdownComponent } from './components/countdown/countdown.component';
import { ProgressBarChartComponent } from './components/progress-bar-chart/progress-bar-chart.component';
import { CombineTranchesComponent } from './components/combine-tranches/combine-tranches.component';
import { TopOrdersTableComponent } from './components/top-orders-card/top-orders-table/top-orders-table.component';
import { SearchByAccountManagerComponent } from './components/search-by-account-manager/search-by-account-manager.component';
import { EntityOrderDetailComponent } from './components/entity-details/entity-details.component';
import { EntityCardComponent } from './components/entity-card/entity-card.component';
import { GamedayRoutingModule } from './gameday-routing.module';
import { TrancheModule } from '../tranche/Tranche.module';
import { BillingService } from './services/billing.service';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { gameDayReducerDef } from '../state/reducer';
import { GamedayDataEffects } from '../state/data/effects';
import { ProgressBarChartEffects } from '../state/charts/effects';
import { ProfileOverviewEffects } from '../state/profile/effects';
import { MonitorEffects } from '../state/monitor/effects';
import { HeaderSidebarComponent } from './components/header/sidebar/sidebar.component';
import { HeaderTitleComponent } from './components/header/title/title.component';
import { ExportReportModalComponent } from './components/excel-export/export-report-modal/export-report-modal.component';
import { FilterBySeriesComponent } from './components/filter-by-series/filter-by-series.component';
import { SealedBidComponent } from './components/sealed-bid/sealed-bid.component';
import { StockToggleComponent } from './components/stock-toggle/stock-toggle.component';
import { ExportReportIconComponent } from './components/excel-export/export-report-icon/export-report-icon.component';
import { ExportService } from './services/export.service';
import { ExportMonitorService } from '../monitor/services/export-monitor.service';
import { AllOrdersModule } from '../all-orders/all-orders.module';
import { ExportOrdersService } from '../all-orders/services/export-orders.service';
import { MonitorModule } from '../monitor/monitor.module';
import { ReportsModule } from '../reports/reports.module';
import { ExportReportsService } from '../reports/services/export-reports.service';
import { MonitorService } from '../monitor/services/monitor.service';
import { MonitorOverviewPageService } from '../monitor/services/monitor-overview-page.service';
import { MonitorRetailPageService } from '../monitor/services/monitor-retail-page.service';
import { MonitorPriorityPageService } from '../monitor/services/monitor-priority-page.service';
import { ManagersOverviewService } from '../reports/services/managers-overview.service';
import { ManagersRetailService } from '../reports/services/managers-retail.service';
import { AccountsReportsService } from '../reports/services/accounts.service';
import { DealComponent } from './components/deal/deal.component';
import { DealResolver } from './resolvers/deal.resolver';
import { RecentOrdersLeftCellRendererComponent } from './components/recent-orders/recent-orders-left-cell-renderer.component';
import { ProfileModule } from '../profile/profile.module';
import { SharedModule } from '@ipreo/muni-shared';
import { AgGridModule } from 'ag-grid-angular';
import { FirstLoadService } from './services/first-load.service';
import { ProgressBarComponent } from './components/deal-summary-card/progress-bar/progress-bar.component';
import { SelectionBarChartComponent } from './components/progress-bar-chart/selection-bar-chart/selection-bar-chart.component';
import { CombineBarChartComponent } from './components/progress-bar-chart/combine-bar-chart/combine-bar-chart.component';
import { DurationPipe } from './pipes/duration.pipe';
import { DropdownsModule, ModalModule } from '@ipreo/ngx-sprinkles';
import { CupcakeDropdownsModule } from '@ipreo/cupcake-components';
import { VersionSelectorComponent } from './components/version-selector/version-selector.component';
import { DealErrorModalComponent } from './components/deal-error-modal-component/deal-error-modal-component';
import { SearchInputComponent } from './components/search-by-account-manager/search-input.component';

@NgModule({
  imports: [
    CommonModule,
    GamedayRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    MarketDrawerModule,
    TrancheModule,
    AllOrdersModule,
    ReportsModule,
    MonitorModule,
    ProfileModule,
    SharedModule,
    EffectsModule.forFeature([
      GamedayDataEffects,
      ProgressBarChartEffects,
      ProfileOverviewEffects,
      MonitorEffects
    ]),
    StoreModule.forFeature('gameDay', gameDayReducerDef),
    ModalModule,
    GamedaySharedModule.forRoot(),
    AgGridModule,
    DropdownsModule,
    CupcakeDropdownsModule
  ],
  declarations: [
    DealErrorModalComponent,
    DealComponent,
    GameDayComponent,
    OverviewComponent,
    CardsHolderComponent,
    DealSummaryCardComponent,
    EntityCardComponent,
    EntityOrderDetailComponent,
    ProgressBarChartComponent,
    RecentOrdersComponent,
    SearchByAccountManagerComponent,
    SearchInputComponent,
    CountdownComponent,
    TopOrdersCardComponent,
    TopOrdersTableComponent,
    CombineTranchesComponent,
    VersionSelectorComponent,
    FilterBySeriesComponent,
    HeaderSidebarComponent,
    HeaderTitleComponent,
    SealedBidComponent,
    StockToggleComponent,
    ExportReportModalComponent,
    ExportReportIconComponent,
    RecentOrdersLeftCellRendererComponent,
    ProgressBarComponent,
    SelectionBarChartComponent,
    CombineBarChartComponent,
    DurationPipe
  ],
  providers: [
    NegativeCasesService,
    ExportService,
    ExportMonitorService,
    ExportOrdersService,
    ExportReportsService,
    MonitorService,
    MonitorOverviewPageService,
    MonitorRetailPageService,
    MonitorPriorityPageService,
    ManagersOverviewService,
    ManagersRetailService,
    AccountsReportsService,
    BillingService,
    DealResolver,
    FirstLoadService
  ],
  // ToDo: Not sure if need, remove later if not
  exports: [
    ExportReportModalComponent,
    ExportReportIconComponent,
    HeaderTitleComponent,
    DealComponent
  ],
  schemas: [NO_ERRORS_SCHEMA, CUSTOM_ELEMENTS_SCHEMA]
})
export class GameDayModule {}
